import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "../../../__generated__";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { notify, notifyError } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { GET_STORE_LIST } from "../store-signup-request/page";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";
import type {
  GetStoreListQuery,
  CreateStoreCouponMutation,
  CreateStoreCouponMutationVariables,
} from "../../../__generated__/graphql";

const CREATE_STORE_COUPON = gql(`
  mutation createStoreCoupon($input: CreateStoreCouponInput!) {
    createStoreCoupon(input: $input) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  GetStoreListQuery["getStoreList"]
>["result"][number];

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function AdminStoreCouponIssue() {
  const [keyword, setKeyword] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [discountValue, setDiscountValue] = useState("");
  const [maxUsageQuantity, setMaxUsageQuantity] = useState("");
  const [date, setDate] = useState(getTodayDate);
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [requireLike, setRequireLike] = useState(false);

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error } = useQuery<GetStoreListQuery>(GET_STORE_LIST);

  const [createStoreCoupon, { loading: isCreating }] = useMutation<
    CreateStoreCouponMutation,
    CreateStoreCouponMutationVariables
  >(CREATE_STORE_COUPON, {
    onCompleted: (res) => {
      if (res.createStoreCoupon.ok) {
        notify("쿠폰이 성공적으로 발급되었습니다.");
        setSelectedStore(null);
        setDiscountValue("");
        setMaxUsageQuantity("");
        setDate(getTodayDate());
        setHour("");
        setMinute("");
        setRequireLike(false);
      } else {
        notifyError(
          res.createStoreCoupon.error ?? "쿠폰 발급 중 오류가 발생했습니다."
        );
      }
    },
    onError: (e) => {
      notifyError(e.message ?? "쿠폰 발급 중 오류가 발생했습니다.");
    },
  });

  const fullList = data?.getStoreList?.ok ? data.getStoreList.result ?? [] : [];
  const keywordLower = keyword.trim().toLowerCase();
  const list = keywordLower
    ? fullList.filter(
        (s) =>
          s.title.toLowerCase().includes(keywordLower) ||
          (s.address?.toLowerCase().includes(keywordLower) ?? false)
      )
    : fullList;

  const handleSubmit = () => {
    if (!selectedStore) {
      notifyError("매장을 선택해주세요.");
      return;
    }
    if (!discountValue || !maxUsageQuantity || !date || !hour || !minute) {
      notifyError("모든 필드를 입력해주세요.");
      return;
    }

    const discountValueNum = parseFloat(discountValue);
    const maxUsageQuantityNum = parseInt(maxUsageQuantity, 10);
    const hourNum = parseInt(hour, 10);
    const minuteNum = parseInt(minute, 10);

    if (isNaN(discountValueNum) || discountValueNum <= 0) {
      notifyError("할인 금액을 올바르게 입력해주세요.");
      return;
    }
    if (isNaN(maxUsageQuantityNum) || maxUsageQuantityNum <= 0) {
      notifyError("선착순 발급 수량을 올바르게 입력해주세요.");
      return;
    }
    if (hourNum < 0 || hourNum > 23) {
      notifyError("시간은 0-23 사이의 값이어야 합니다.");
      return;
    }
    if (minuteNum < 0 || minuteNum > 59) {
      notifyError("분은 0-59 사이의 값이어야 합니다.");
      return;
    }

    createStoreCoupon({
      variables: {
        input: {
          storeId: selectedStore.id,
          discountValue: discountValueNum,
          maxUsageQuantity: maxUsageQuantityNum,
          date,
          hour: hourNum,
          minute: minuteNum,
          conditions: requireLike ? [{ type: "LIKE_REQUIRED" }] : [],
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="flex justify-center py-8">
          <LoadingSpinnerInline />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }
  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="매장 전용 쿠폰 발급"
      description="매장을 선택하고 쿠폰 정보를 입력한 뒤 발급하세요."
      leftRatio={7}
      rightRatio={3}
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명·주소로 검색",
        searchValue: keyword,
        onSearchChange: setKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={list.map((s) => ({
              id: s.id,
              title: s.title,
              img: s.img,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) =>
              setSelectedStore(list.find((x) => x.id === store.id) ?? null)
            }
            emptyText={
              keywordLower
                ? "검색 결과가 없습니다."
                : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "쿠폰 발급",
        emptyText: "매장을 선택하세요.",
        children: selectedStore ? (
          <div className="space-y-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">선택 매장:</span>{" "}
              {selectedStore.title} (ID: {selectedStore.id})
            </div>

            <div className="flex items-center gap-3">
              {selectedStore.img ? (
                <ResponsiveCDNImage
                  src={selectedStore.img}
                  alt={selectedStore.title}
                  className="w-16 h-16 rounded object-cover"
                  sizeRatio={0.16}
                />
              ) : (
                <div className="w-16 h-16 bg-gray-200 rounded" />
              )}
              <div>
                <div className="font-medium text-gray-800">
                  {selectedStore.title}
                </div>
                <div className="text-sm text-gray-600">
                  {selectedStore.address ?? "—"}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  할인 금액 (원) *
                </label>
                <input
                  type="number"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder="예: 1000"
                  min={0}
                  step={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  선착순 발급 수량 *
                </label>
                <input
                  type="number"
                  value={maxUsageQuantity}
                  onChange={(e) => setMaxUsageQuantity(e.target.value)}
                  placeholder="예: 100"
                  min={1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  만료 날짜 *
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    시 *
                  </label>
                  <input
                    type="number"
                    value={hour}
                    onChange={(e) => setHour(e.target.value)}
                    placeholder="0-23"
                    min={0}
                    max={23}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    분 *
                  </label>
                  <input
                    type="number"
                    value={minute}
                    onChange={(e) => setMinute(e.target.value)}
                    placeholder="0-59"
                    min={0}
                    max={59}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="pt-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  발급 조건
                </p>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={requireLike}
                    onChange={(e) => setRequireLike(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    알림받기(좋아요) 필수
                  </span>
                </label>
                {requireLike && (
                  <p className="mt-1 text-xs text-blue-600 pl-6">
                    이 매장에 알림받기를 한 고객만 쿠폰을 받을 수 있습니다.
                    미충족 고객이 받기를 누르면 알림받기 후 자동 발급됩니다.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setSelectedStore(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-md hover:bg-gray-200 text-sm"
              >
                선택 해제
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isCreating}
                className="flex-1 px-4 py-2 bg-blue-500 text-white font-medium rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              >
                {isCreating ? (
                  <>
                    <LoadingSpinnerInline />
                    발급 중...
                  </>
                ) : (
                  "쿠폰 발급"
                )}
              </button>
            </div>
          </div>
        ) : null,
      }}
    />
  );
}
